import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  createAdminFeed,
  disableAdminFeed,
  enableAdminFeed,
  hardDeleteAdminFeed,
  importAdminFeedsCsv,
  listAdminFeeds,
  softDeleteAdminFeed,
  updateAdminFeed,
  validateAdminFeedUrl,
} from '../services/feedAdminApi';
import {
  CANONICAL_FEED_CATEGORIES,
  FEED_WEIGHT_STEPS,
  snapFeedWeight,
} from '../constants/feedCategories';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './ResearchPage.css';
import './AdminFeedsPage.css';

ModuleRegistry.registerModules([AllCommunityModule]);

/** Grid select values: blank = no override; include legacy "none". */
const CATEGORY_SELECT_VALUES = ['', 'none', ...CANONICAL_FEED_CATEGORIES];
const WEIGHT_SELECT_VALUES = FEED_WEIGHT_STEPS.map((w) => w.toFixed(1));

const emptyDraft = {
  url: '',
  name: '',
  category: '',
  feedWeight: 1,
  enabled: true,
};

const AdminFeedsPage = () => {
  useDocumentTitle('Config');
  const { user, authReady, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [rowData, setRowData] = useState([]);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [importFile, setImportFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const feeds = await listAdminFeeds();
    setRowData(Array.isArray(feeds) ? feeds : []);
  }, []);

  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!isAuthenticated || user?.principalName !== 'admin') {
      navigate('/login', { replace: true });
      return;
    }
    reload().catch((e) => setError(e.message));
  }, [authReady, isAuthenticated, user, navigate, reload]);

  const categorySelectValues = useMemo(() => {
    const extras = new Set();
    rowData.forEach((feed) => {
      const cat = (feed.category || '').trim();
      if (cat && !CATEGORY_SELECT_VALUES.includes(cat)) {
        extras.add(cat);
      }
    });
    return [...CATEGORY_SELECT_VALUES, ...Array.from(extras).sort()];
  }, [rowData]);

  const columnDefs = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        editable: true,
        flex: 1.2,
        minWidth: 140,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'startsWith', 'endsWith', 'equals', 'notContains'],
          defaultOption: 'contains',
          caseSensitive: false,
          trimInput: true,
        },
      },
      {
        field: 'url',
        headerName: 'URL',
        editable: true,
        flex: 2,
        minWidth: 220,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'startsWith', 'endsWith', 'equals', 'notContains'],
          defaultOption: 'contains',
          caseSensitive: false,
          trimInput: true,
        },
      },
      {
        field: 'category',
        headerName: 'Category',
        editable: true,
        width: 150,
        singleClickEdit: true,
        filter: 'agSetColumnFilter',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: categorySelectValues,
        },
        valueFormatter: (p) => {
          const v = (p.value || '').trim();
          if (!v) return '— none —';
          if (v === 'none') return 'none (no override)';
          return v;
        },
        valueParser: (p) => String(p.newValue ?? '').trim(),
      },
      {
        field: 'feedWeight',
        headerName: 'Weight',
        editable: true,
        width: 110,
        singleClickEdit: true,
        filter: 'agSetColumnFilter',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: WEIGHT_SELECT_VALUES,
        },
        valueGetter: (p) => snapFeedWeight(p.data?.feedWeight).toFixed(1),
        valueSetter: (p) => {
          const next = snapFeedWeight(p.newValue);
          if (p.data.feedWeight === next) return false;
          p.data.feedWeight = next;
          return true;
        },
        valueFormatter: (p) => snapFeedWeight(p.value).toFixed(1),
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        editable: true,
        width: 110,
        cellDataType: 'boolean',
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'Actions',
        width: 365,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params) => {
          const id = params.data?.id;
          if (!id) return null;
          return (
            <div className="admin-feeds-actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => params.context.onValidate(params.data)}
              >
                Validate
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => params.context.onToggle(params.data)}
              >
                {params.data.enabled ? 'Disable' : 'Enable'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => params.context.onSoftDelete(params.data)}
              >
                Soft-delete
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => params.context.onHardDelete(params.data)}
              >
                Hard-delete
              </button>
            </div>
          );
        },
      },
    ],
    [categorySelectValues],
  );

  const onCellValueChanged = async (event) => {
    const feed = event.data;
    if (!feed?.id) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await updateAdminFeed(feed.id, {
        url: feed.url,
        name: feed.name,
        category: feed.category || '',
        feedWeight: snapFeedWeight(feed.feedWeight ?? 1),
        enabled: Boolean(feed.enabled),
        description: feed.description,
        notes: feed.notes,
        language: feed.language,
        country: feed.country,
        sourceType: feed.sourceType,
        fetchIntervalMinutes: feed.fetchIntervalMinutes,
        maxArticlesPerFetch: feed.maxArticlesPerFetch,
      });
      setStatus(`Saved ${feed.name || feed.url}`);
      await reload();
    } catch (e) {
      setError(e.message);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const context = useMemo(
    () => ({
      onValidate: async (feed) => {
        setBusy(true);
        setError(null);
        setStatus(null);
        try {
          const result = await validateAdminFeedUrl(feed.url);
          if (result.validRss) {
            const access = result.articleAccess || 'unknown';
            const use = result.usability || 'unknown';
            setStatus(
              `RSS OK · ${use} · article ${access} — ${result.feedTitle || feed.url} (${result.entryCount} entries, HTTP ${result.httpStatus})`,
            );
          } else {
            setError(
              `Dead/invalid RSS: ${result.failureReason || 'unknown'} (HTTP ${result.httpStatus ?? 'n/a'}) · usability ${result.usability || 'dead'}`,
            );
          }
        } catch (e) {
          setError(e.message);
        } finally {
          setBusy(false);
        }
      },
      onToggle: async (feed) => {
        setBusy(true);
        setError(null);
        try {
          if (feed.enabled) {
            await disableAdminFeed(feed.id);
          } else {
            await enableAdminFeed(feed.id);
          }
          await reload();
        } catch (e) {
          setError(e.message);
        } finally {
          setBusy(false);
        }
      },
      onSoftDelete: async (feed) => {
        if (!window.confirm(`Disable feed?\n${feed.url}`)) return;
        setBusy(true);
        setError(null);
        try {
          await softDeleteAdminFeed(feed.id);
          setStatus(`Disabled ${feed.url}`);
          await reload();
        } catch (e) {
          setError(e.message);
        } finally {
          setBusy(false);
        }
      },
      onHardDelete: async (feed) => {
        if (!window.confirm(`Permanently delete feed? This cannot be undone.\n${feed.url}`)) return;
        setBusy(true);
        setError(null);
        try {
          await hardDeleteAdminFeed(feed.id);
          setStatus(`Permanently deleted ${feed.url}`);
          await reload();
        } catch (e) {
          setError(e.message);
        } finally {
          setBusy(false);
        }
      },
    }),
    [reload],
  );

  const handleCreate = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const created = await createAdminFeed({
        url: draft.url.trim(),
        name: draft.name.trim() || undefined,
        category: draft.category.trim() || undefined,
        feedWeight: snapFeedWeight(draft.feedWeight),
        enabled: Boolean(draft.enabled),
      });
      setDraft(emptyDraft);
      setStatus(`Created ${created.name || created.url}`);
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleValidateDraft = async () => {
    if (!draft.url.trim()) {
      setError('URL is required to validate');
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await validateAdminFeedUrl(draft.url.trim());
      if (result.validRss) {
        const access = result.articleAccess || 'unknown';
        const use = result.usability || 'unknown';
        setStatus(
          `RSS OK · ${use} · article ${access} — ${result.feedTitle || draft.url} (${result.entryCount} entries)`,
        );
        if (result.feedTitle && !draft.name) {
          setDraft((d) => ({ ...d, name: result.feedTitle }));
        }
      } else {
        setError(
          `Dead/invalid RSS: ${result.failureReason || 'unknown'} · usability ${result.usability || 'dead'}`,
        );
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const summarizeImport = (result) =>
    `${result.dryRun ? 'Dry run' : 'Import'}: ${result.inserted} insert, ${result.updated} update, ${result.skipped} skip, ${result.failed} failed, ${result.repairedRows} repaired`;

  const handleImportCsv = async (dryRun) => {
    if (!importFile) {
      setError('Choose a feeds.csv file first');
      return;
    }
    if (!dryRun && !window.confirm(`Import ${importFile.name} into Mongo rss_feeds?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await importAdminFeedsCsv(importFile, { dryRun, updateExisting });
      const issuePreview = (result.issues || [])
        .slice(0, 3)
        .map((issue) => `line ${issue.lineNumber}: ${issue.message}`)
        .join('; ');
      setStatus(issuePreview ? `${summarizeImport(result)} — ${issuePreview}` : summarizeImport(result));
      if (!dryRun) {
        await reload();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="research-page admin-feeds-page">
      <div className="container">
        {!authReady ? (
          <p>Checking authentication…</p>
        ) : (
          <>
        <div className="admin-feeds-header">
          <h1>Config</h1>
          <Link to="/admin/agents">Agents</Link>
          <Link to="/admin/pipeline-ops">Pipeline Ops</Link>
        </div>
        <p className="admin-feeds-help">
          RSS feeds today; other site settings will land here later. Mongo <code>rss_feeds</code> is
          the source of truth. Feed <strong>category</strong> is a
          search-filter override only (not LLM story labels). Empty / <code>none</code> = no
          override — desk uses story <code>categories[]</code>. Weight is 0–1 in 0.1 steps
          (default 1). Click category or weight cells to pick from the dropdown; saves on change.
          Use the filter row under headers (Name/URL are text search) to narrow the list.
        </p>
        {error && <p className="research-error">{error}</p>}
        {status && <p className="admin-feeds-status">{status}</p>}
        {busy && <p className="admin-feeds-busy">Working…</p>}

        <form className="admin-feeds-create" onSubmit={handleCreate}>
          <h2>Add feed</h2>
          <div className="admin-feeds-create-row">
            <input
              type="url"
              required
              placeholder="https://example.com/rss"
              value={draft.url}
              onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
            />
            <input
              type="text"
              placeholder="Name"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
            <select
              className="admin-feeds-select"
              value={draft.category}
              onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
              title="Optional search-filter override; leave empty to use story categories"
              aria-label="Feed category"
            >
              <option value="">— no override —</option>
              <option value="none">none (no override)</option>
              {CANONICAL_FEED_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <select
              className="admin-feeds-select admin-feeds-weight"
              value={snapFeedWeight(draft.feedWeight).toFixed(1)}
              onChange={(e) =>
                setDraft((d) => ({ ...d, feedWeight: snapFeedWeight(e.target.value) }))
              }
              title="feedWeight (0–1, step 0.1)"
              aria-label="Feed weight"
            >
              {WEIGHT_SELECT_VALUES.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
            <label className="admin-feeds-enabled">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
              />
              Enabled
            </label>
            <button type="button" className="btn btn-outline-secondary" onClick={handleValidateDraft}>
              Validate
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              Create
            </button>
          </div>
        </form>

        <section className="admin-feeds-import">
          <h2>Import feeds.csv</h2>
          <p>
            Seed/emergency import only. Mongo stays source of truth; existing URLs are skipped
            unless update is enabled.
          </p>
          <div className="admin-feeds-create-row">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
            <label className="admin-feeds-enabled">
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
              />
              Update existing rows
            </label>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={busy || !importFile}
              onClick={() => handleImportCsv(true)}
            >
              Dry-run CSV
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={busy || !importFile}
              onClick={() => handleImportCsv(false)}
            >
              Import CSV
            </button>
          </div>
        </section>

        <div className="ag-theme-alpine-dark admin-feeds-grid">
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            context={context}
            getRowId={(p) => p.data.id}
            onCellValueChanged={onCellValueChanged}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true,
              floatingFilter: true,
            }}
            animateRows
            domLayout="autoHeight"
          />
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminFeedsPage;
